import { Chip, Tooltip as MaterialTooltip,Box } from "@mui/material";
import ErrorIcon from '@mui/icons-material/Error';
import { isArray,orderBy } from "lodash";
import moment from 'moment';
import { ComponentProps, default as React } from "react";
import { OverlayTrigger, ProgressBar, ProgressBarProps, Tooltip } from 'react-bootstrap';
import { Link } from "react-router-dom";
import { prependZero } from "..";
import { PopoverLink } from "../../common";
import { APIKeyShowComponent } from "../../pages/administration/APIKeysPage/APIKeyShowComponent";
import { CopyButton } from "../../common/CopyButton";
import { CONFIG_TYPE_EAPG, CONFIG_TYPE_SNF, CONFIG_TYPE_IRF, CONFIG_TYPE_IPF, CONTRACT_STATE_FINALIZED, CONTRACT_STATE_MODEL, RULE_TYPE_CARVEOUT, RULE_TYPE_DEFAULT, RULE_TYPE_RULE, CONFIG_TYPE_LTCH } from "../../store/constants/contract";
import { dateEquators, StringEquators } from "../../store/constants/pages";
import { asDate, convertDateToLocal, parseDate } from '../../util/date';
import { transformOptions } from "../../common";

export type AnyProps = Record<string, any>;

export const createStringFieldFromArrayJoin = <T extends AnyProps>(id: string, label: string, keyName: string, options: T = {} as T) => {
    // return {
    //     id,
    //     label,
    //     renderString: (value: any[]) => {
    //         if(keyName !== null) {
    //             return value.filter((value: any) => value !== null).map((item: { [x: string]: any; }) => item[keyName] ?? "").join(', ')
    //         }
    //         else {
    //             return value.filter((value: any) => value !== null).join(', ')
    //         }
    //     },
    //     options
    // };



    return transformOptions({
        id,
        label,
        renderString: (value: any[]) => {
            if(keyName !== null) {
                return value.filter((value: any) => value !== null).map((item: { [x: string]: any; }) => item[keyName] ?? "").join(', ')
            }
            else {
                return value.filter((value: any) => value !== null).join(', ')
            }
        },
        options
  
    },{
        renderString: 'valueFormatter',
        type: 'singleSelect',
        valueGetter: x => x == null ? 'null' : x,
        valueSetter: x => x === 'null' ? null : x,
        valueOptions: null
    });
}

export const createStringFieldFromArrayMapping = <T extends AnyProps>(id: string, label: string, dataArray: any[], keyName: string, options: T = {} as T) => {
    
    return {
        id,
        label,
        renderString: (value: any[]) => {
            const filteredArray = dataArray.filter((arrayItem: { id: any; }) => value.some((y: { [x: string]: any; }) => y[keyName] == arrayItem.id));
            return filteredArray.map((item: { name: any; }) => item.name).join(', ')
        },
        options
    };
}

export const createStringFieldFromArray = <T extends AnyProps>(id: string, label: string, options: T = {} as T,defaultObj=[]) => {
    
    return transformOptions({
        id,
        label,
        renderString: (value: any[]) => {
            return options?.maxLength && value.length > options.maxLength ?
                (defaultObj? [defaultObj?.find((item)=>item?.id==value.slice(0, options.maxLength)[0])?.name]:value.slice(0, options.maxLength)).join(', ') + `, and ${value.length - options.maxLength} more` :
                defaultObj ? [defaultObj?.find((item)=>item?.id==value[0])?.name || value].join(', ') :value.join(', ')
        },
        options
    },{
        renderString: 'valueGetter',
        type: 'string',
    })
}



export const createReadableStringFieldFromArray = <T extends AnyProps>(
    id: string,
    label: string,
    options: T = {} as T,
    defaultObj = []
) => {
    return transformOptions(
        {
            id,
            label,
            renderString: (value: any[]) => {
                if (!Array.isArray(value) || value.length === 0) return '';

                const maxLength = options?.maxLength || 1;

                // Keep only valid IDs found in defaultObj
                const validItems = value.filter((v) =>
                    defaultObj?.some((item) => item?.id == v)
                );

                if (validItems.length === 0) return '';

                // Take first `maxLength` visible items
                const visibleItems = validItems.slice(0, maxLength);
                const visibleNames = visibleItems.map(
                    (v) => defaultObj?.find((item) => item?.id == v)?.name || v
                );

                // Count remaining valid items
                const remainingCount = validItems.length - maxLength;

                // Build final display string
                if (remainingCount > 0) {
                    return `${visibleNames.join(', ')}, and ${remainingCount} more`;
                }

                return visibleNames.join(', ');
            },
            options,
        },
        {
            renderString: 'valueGetter',
            type: 'string',
        }
    );
};

/**
 * Creates a field for rendering nested array data with property access
 * Similar to createStringFieldFromArrayJoin but supports nested paths like "contract.name"
 * @param id - The field id (array property name)
 * @param label - Display label
 * @param nestedPath - Path to the nested property (e.g., "contract.name" for item.contract.name)
 * @param options - Additional options
 * @returns Field configuration object
 * 
 * @example
 * createNestedArrayField("suppKBDocumentMetadataContractMap", "Contracts", "contract.name")
 * // Maps through array and extracts contract.name from each item, joins with ", "
 */
export const createNestedArrayField = <T extends AnyProps>(
    id: string,
    label: string,
    nestedPath: string,
    options: T = {} as T
) => {
    return transformOptions(
        {
            id,
            label,
            renderString: (value: any[]) => {
                if (!Array.isArray(value) || value.length === 0) return '';

                // Split the nested path (e.g., "contract.name" => ["contract", "name"])
                const pathParts = nestedPath.split('.');

                // Map through array and access nested property
                return value
                    .filter((item: any) => item !== null)
                    .map((item: any) => {
                        let result = item;
                        // Navigate through the path
                        for (const part of pathParts) {
                            result = result?.[part];
                            if (result === undefined || result === null) return null;
                        }
                        return result;
                    })
                    .filter(Boolean)
                    .join(', ');
            },
            options,
        },
        {
            renderString: 'valueFormatter',
            type: 'singleSelect',
            valueGetter: x => x == null ? 'null' : x,
            valueSetter: x => x === 'null' ? null : x,
            valueOptions: null
        }
    );
};



export const createStringFieldFromObjectMapping = <T extends AnyProps, U extends AnyProps>(id: string, label: string, dataObject: T, options: U = {} as U) => {
    return transformOptions({
        id,
        label,
        renderString: (value: string | number) => {
            return dataObject[value]
        },
        options
    },{
        renderString: 'valueFormatter',
        type: 'singleSelect',
        valueGetter: x => x == null ? 'null' : x,
        valueSetter: x => x === 'null' ? null : x,
        valueOptions: Object.keys(dataObject).map((key) => ({ value: key, label: dataObject[key] }))
    });
}

export const createDocumentTypeField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value) => {
            // If value is null or undefined, default to "Contract"
            if (value === null || value === undefined || value === 1) {
                return 'Contract';
            }
            return value === 2 ? 'Appeal Letter' : 'Contract';
        },
        options
    }, {
        type: 'string',
        renderString: false,
    });
};

export const createStringDataField = <T extends AnyProps>(id: string, label: string, options : T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {

            if (options?.isAddressField) {
                return [
                    value?.address,
                    value?.streetAddress,
                    value?.city,
                    value?.state,
                    value?.zip,
                ]
                    .filter(Boolean)
                    .join(", ");
            }

            if (typeof value === 'string') {
                if (value) {
                    return value;
                }
                return '';
            }
            if (typeof value === 'number') {
                if (value) {
                    return value;
                }
                return '';
            }

            if (typeof value === 'object') {
                const { secondLevel } = options;

                if (options?.thirdLevel) {
                    if (value && value[secondLevel][options?.thirdLevel]) {
                        return value[secondLevel][options?.thirdLevel];
                    }
                    return "";
                }
                if (value && value[secondLevel]) {
                    return value[secondLevel];
                }
                return '';
            }
        },
        options
    },{
        type: 'string',

        ...( options.nullable ? { 
            valueGetter: x => x == null ? '' : x,
            valueSetter: x => x === '' ? null : x,        
        } : {} ),

        renderString: false,
    });
}

export const createStringDataFieldwithEllipis = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value:any) => {


            const createEllipisis = (truncatedValue,value) =>  (
                        <span>
                            {value.length > options?.ellipisisWidth ? (
                                <>
                                    <span ref={(el) => {
                                        if (el && el.textContent === truncatedValue) {
                                            el.setAttribute('data-full-text', value);
                                        }
                                    }}>
                                        {truncatedValue}
                                    </span>
                                    <button
                                        style={{ background: 'none', border: 'none', color: 'blue', cursor: 'pointer' }}
                                        onClick={(e) => {
                                            if(options?.handleEllipisisShow){
                                                options?.handleEllipisisShow(value,label,fullObj);
                                                return
                                            }
                                            const el = e.target as HTMLButtonElement;
                                            const textElement = el.previousSibling as HTMLElement;
                                            
                                            const fullText = textElement.getAttribute('data-full-text')!;
                                            
                                            if (textElement.textContent === fullText) {
                                                textElement.textContent = truncatedValue;
                                                el.textContent = 'Show more';
                                            } else {
                                                textElement.textContent = fullText;
                                                el.textContent = 'Show less';
                                            }
                                        }}
                                    >
                                        Show more
                                    </button>
                                </>
                            ) : (
                                value
                            )}
                        </span>
                    
                        )

            let fullObj={...value}
            value=value[id]

            if (options?.secondLevel  ) {
                const { secondLevel } = options;
                const firstLevel=id?.split('.')[0]
                if (fullObj && fullObj[firstLevel][secondLevel]) {
                    const fullValue= fullObj[firstLevel][secondLevel];
                    const truncatedValue = fullValue.length > options?.ellipisisWidth ? `${fullValue.slice(0, options?.ellipisisWidth)}...` : fullValue;
                    return createEllipisis(truncatedValue,fullValue);
                    
                }
                return '';
            }

          if (typeof value === 'string') {
                if (value) {
                    const truncatedValue = value.length > options?.ellipisisWidth ? `${value.slice(0, options?.ellipisisWidth)}...` : value;

                   return createEllipisis(truncatedValue,value);
                }
                return '';
            }
            if (typeof value === 'number') {
                if (value) {
                    return value;
                }
                return '';
            }

            
        },
        options
    },{
        type: 'string',

        ...(options.nullable ? {
            valueGetter: x => x == null ? '' : x,
            valueSetter: x => x === '' ? null : x,
        } : {}),

        renderString: 'renderCell',
    });
}


export const createConditionalStringDataField = <T extends AnyProps>(id: string, label: string,condition,id2, options : T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {

            if (typeof value === 'string') {
                if (value) {
                    return value;
                }
                return '';
            }
            if (typeof value === 'number') {
                if (value) {
                    return value;
                }
                return '';
            }

            if (typeof value === 'object') {
                const { secondLevel } = options;
                if (value && value[secondLevel]) {
                    return value[secondLevel];
                }
                return '';
            }
        },
        condition,
        id2,
        options
    },{
        type: 'string',

        ...( options.nullable ? { 
            valueGetter: x => x == null ? '' : x,
            valueSetter: x => x === '' ? null : x,        
        } : {} ),

        renderString: false,
    });
}

export const createClaimNumberFieldWithCopyButton = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({      
        id,
        label,
        renderString: (value: { claimnumber: string; id: any; claimAnalysisId: any; }) => {
            let text = '';
            if (value) {
                text = value.claimnumber;
            }

            return <CopyButton text={text} claimId={value.claimAnalysisId ? value.claimAnalysisId : value.id ?? 'null'} isFromTriage={options?.isFromTriage} />
        },
        options
    },{
        type: 'string', 
        renderString: 'renderCell',
    });
}

export const createClaimAnalysisPatientNameField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { patientLastname: string; patientFirstname: any; patientMiddlename: any; }) => {
            return value?.patientLastname ? value?.patientLastname + ', ' + `${value?.patientFirstname ?? ''} ${value?.patientMiddlename ?? ''}` : ''
        },
        options
    }, {
        type: 'string', 
        renderString: 'valueGetter',
    });
}
export const createClaimAnalysisSubscriberNameField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { snResponsecontactlastororganizationname: string; snResponsecontactfirstname: any; snResponsecontactmiddlename: any; }) => {
            return value?.snResponsecontactlastororganizationname ? value?.snResponsecontactlastororganizationname + ', ' + `${value?.snResponsecontactfirstname ?? ''} ${value?.snResponsecontactmiddlename ?? ''}` : ''
        },
        options
    }, {
        type: 'string', 
        renderString: 'valueGetter',
    });
}

export const createCommentsField = <T extends AnyProps>(id: string, label: string, options : T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {
            if (value) {
                return  <MaterialTooltip title={value}>
                            <span>{value.length > 50 ? value.slice(0,50) +'...' : value}</span>
                        </MaterialTooltip>
            }
            return '';
        },
        options
    },{
        type: 'string',
        renderString: 'renderCell',
    });
}

export const createClaimAnalysisPhysicianNameField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { physicianLastname: any; physicianFirstname: any; }) => {
            return value?.physicianLastname ? `${value?.physicianLastname}, ${value?.physicianFirstname ?? ''}` : ''
        },
        options
    },{
        type: 'string', 
        renderString: 'valueGetter',
    });
}

export const createStringFieldFromMultiKeys = <T extends object>(keyArray: string[], label: string, options: T = {} as T) => {
    return transformOptions({
        id: options['sortColumn'] || label,
        label,
        renderString: (value: { [x: string]: any; }) => {
            let str = '';
            for (let item of keyArray) {
                str = `${str} ${value[item] ?? ''}`;
                if (value[item] && options['commaSeparated']) {
                    str += ',';
                }
            }
            return str;
        },
        options
    },{
        type: 'string', 
        renderString: "valueGetter",
    });
}

const renderTooltip = (props: React.ComponentProps<typeof Tooltip>, columnData: AnyProps, numerator: string, denominator: string, name: any) => {
    const isFailed = columnData.state === 'FAILED';
    return (
        <Tooltip id="button-tooltip" {...props as any}>
            {isFailed ? `${columnData.exception}` : `${columnData[numerator]} of ${columnData[denominator]} ${name}`}
        </Tooltip>
    );
}

const popover = (props: React.ComponentProps<typeof Tooltip>, columnData: AnyProps) => {
    return (
        <Tooltip id="button-tooltip" {...props as any}>
            {`${columnData.exception}`}
        </Tooltip>
    );
}

export const createProgressBarField = <T extends AnyProps>(id: string, label: string, numerator: string, denominator: string, name: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: AnyProps) => {
            let color: ProgressBarProps['variant'] = 'info';
            const {state,exception} = value;

            let percentage = 0;
            let label = '';
            if (state === 'FAILED') {
                percentage = 100;
                label = 'Failed';
            } else if (state === 'SUBMITTED') {
                percentage = 0;
            } else {
                percentage = ((value[numerator] * 100) / value[denominator]) || 0;
                label = `${(percentage?.toFixed())}%`;
            }
            switch (state) {
                case 'COMPLETED':
                    color = 'success';
                    break;
                case 'INPROGRESS':
                    color = 'info';
                    break;
                case 'FAILED':
                    color = 'danger';
                    break;
                case 'SUBMITTED':
                    color = 'info';
                    break;
                default:
                    color = 'warning';
                    break;
            }

            return (
                <>
                    {
                        (value.state !== 'FAILED' && (value[numerator] === null || value[denominator] === null)) ?
                            (<ProgressBar className="progress-min-width" label={label} variant={color} now={percentage} />) :
                            color === 'danger' ? (
                                <OverlayTrigger 
                                
                                placement="right"
                            overlay={(props: any) => (
                                <Tooltip
                                    id="button-tooltip"
                                    {...(props as any)}
                                >
                                    {exception}
                                </Tooltip>
                            )}
                                >
                                    <ProgressBar className="progress-min-width" label={label} variant={color} now={percentage} />
                                </OverlayTrigger>
                            ) : color === 'warning' ?
                                <OverlayTrigger
                                    placement="right"
                                    overlay={(props: any) => renderTooltip(props, value, numerator, denominator, name)}
                                >
                                    <ProgressBar className="progress-min-width warning-progress-bar font-weight-600" label={label} now={percentage} />
                                </OverlayTrigger> :
                                <OverlayTrigger
                                    placement="right"
                                    overlay={(props: any) => renderTooltip(props, value, numerator, denominator, name)}
                                >
                                    <ProgressBar className="progress-min-width" label={label} variant={color} now={percentage} />
                                </OverlayTrigger>
                    }
                </>

            );
        },
        options: {
            ...options,
            returnWholeRow: true,
            hideFromFilter: true,
            width: 130
        }
    };
}

export const createStatusFieldAndPopper = <T extends AnyProps>(
    id: string,
    label: string,
    options: T = {} as T
) => {
    return {
        id,
        label,
        renderString: (value: AnyProps) => {
            let color: ProgressBarProps["variant"] = "info";
            const { state,status, exception,eventDetails } = value;

            switch (state || status) {
                case "COMPLETED":
                case "Success":
                    color = "success";
                    break;
                case "INPROGRESS":
                    color = "info";
                    break;
                case "FAILED":
                case "Failed":
                    color = "danger";
                    break;
                case "SUBMITTED":
                    color = "info";
                    break;
                default:
                    color = "warning";
                    break;
            }

            if (state !== "FAILED" && status!="Failed") {
                return (
                    <ProgressBar
                        className="progress-min-width font-weight-600"
                        variant={color}
                        label={state ||  status}
                        now={100}
                    />
                );
            }

            return (
                <>
                    {
                        <OverlayTrigger
                            placement="right"
                            overlay={(props: any) => (
                                <Tooltip
                                    id="button-tooltip"
                                    {...(props as any)}
                                >
                                    {exception  ||  eventDetails}
                                </Tooltip>
                            )}
                        >
                            <ProgressBar
                                className="progress-min-width font-weight-600"
                                variant={color}
                                label={state ||  status}
                                now={100}
                            />
                        </OverlayTrigger>
                    }
                </>
            );
        },
        options: {
            ...options,
            returnWholeRow: true,
            hideFromFilter: true,
            width: 130,
        },
    };
};

export const createBooleanDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => {
            if (typeof value === 'boolean') {
                if (value) {
                    return 'Active';
                } else {
                    return 'In Active'
                }
            }
            return '';
        },
        options
    },{
        type: 'boolean', 
        renderString: false,
    });
}

export const createPlainDateField = <T extends AnyProps>(id: string, label: string, options : T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: moment.MomentInput) =>{
            try {
                return typeof value === "string" && value
                    ? options?.isString
                        ? value
                        : asDate(value)
                    : "N/A";
            } catch (e) {
                return value;
            }
        },
        options
    },{
        type: 'date', 
        renderString: false,
        valueGetter: x => parseDate( x, 'plain-date' )
    });
}

export const createDateField = <T extends AnyProps>(id: string, label: string, options : T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: moment.MomentInput) => (typeof value === 'string' && value) ? asDate( new Date( value ) ) : 'N/A',
        options
    }, {
        type: 'date', 
        renderString: false,
        valueGetter: x => parseDate( x, 'date-time' )
    })
}

export const createTimestampField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => {
            return (typeof value === 'string' && value) ? convertDateToLocal(value) : 'N/A';
        },
        options
    },{
        type: 'dateTime', 
        renderString: false,
        valueGetter: x => parseDate( x, 'date-time' )
    });
}

export const createTimeDifferenceField = <T extends AnyProps>(startTime: string, endTime: string, label: string, options: T = {} as T) => {
    return {
        id: startTime,
        label,
        renderString: (value: { [x: string]: moment.MomentInput; }) => {
            const diff = moment.duration(moment(value[endTime]).diff(moment(value[startTime])));
            const hh = diff.get("hours");
            const mm = diff.get("minutes");
            const ss = diff.get("seconds");
            if (hh === 0 && mm === 0) {
                return `${prependZero(ss)} sec`
            } else if (hh === 0 && mm > 0) {
                return `${prependZero(mm)}:${prependZero(ss)}`;
            } else {
                return `${prependZero(hh)}:${prependZero(mm)}:${prependZero(ss)}`
            }
        },
        options
    };
}

export const createBucketNameField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any[]) => (value) ? <ul className="mb-0 pl-4"> {value.map((item: { name: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal; }, index: React.Key) => { return (<li key={index}>{item.name}</li>) })} </ul> : '',
        options
    };
}

export const createBucketNameListField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any[]) => (value) ? <ul style={{ paddingInlineStart: '15px' }} className="mb-0">
            {value.map((item: { bucketName: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal; }, index: { toString: () => any; }) =>
                <li style={{ paddingBottom: '3px' }} key={`bucket-${index.toString()}`}><b>{item.bucketName}</b>
                </li>
            )}
        </ul> : '',
        options
    };
}

export const createBucketStatusListField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any[]) => (value) ? <ul style={{ paddingInlineStart: '10px', listStyleType: 'none' }} className="mb-0">
            {value.map((item: { state: {}; }, index: any) =>
                <li style={{ paddingBottom: '3px' }}>
                    <MaterialTooltip title={item.state}>
                        <i title='text' style={{ fontSize: '1.3rem' }} className={item.state === 'ENABLED' ? "fas fa-check-circle text-success" : item.state === 'DISABLED' ? "fas fa-times-circle text-danger" : "fas fa-exclamation-circle text-secondary"}></i>
                    </MaterialTooltip>
                    
                </li>
            )}
        </ul> : '',
        options
    };
}

export const createOrderDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: number) => (value !== 99999) ? value : '-',
        options
    };
}

export const createClientListField = <T extends { clients: { clientId: any; name: string; }[] }>(id: string, label: string, options: T) => {
    return {
        id,
        label,
        renderString: (value: any[], rowData: any) => {
            const { clients } = options;

            if (value && Array.isArray(value)) {
                const clientNames = orderBy(
                    value
                        .map((item: any) =>
                            clients.find(
                                (client: { clientId: any }) => client.clientId === item
                            )?.name
                        )
                        .filter(Boolean), 
                    [(name) => name.toLowerCase()],
                    ['asc']
                );
                return (
                    <ul>
                        {clientNames.map((name, index) => (
                            <li key={index}>{name}</li>
                        ))}
                    </ul>
                );
            }
            return '';
        },
        options
    };
}

export const createNumberDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => (typeof value === 'number' && value) ? value : 0,
        options
    },{
        type: 'number', 
        renderString: 'valueFormatter',    
        valueFormatter : options.valueFormatter
    });
}

export const createNumberNullableDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => (typeof value === 'number' && value) ? value : 'N/A',
        options
    },{
        type: 'number', 
        renderString: 'valueFormatter',    
        valueFormatter : options.valueFormatter
    });
}

export const createNullableNumberDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => value === null || value === undefined ? 'N/A' : value,
        options
    },{
        type: 'number',
        renderString: 'valueFormatter',
        valueFormatter : options.valueFormatter
    });
}

export const createCurrencyDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) =>
            typeof value === "number" ? (
                <Box component="span" sx={{ display: "block", minWidth: "max-content" }}>
                    {`$ ${createFloatingPoint(value)}`}{" "}
                </Box>
            ) : (
                "N/A"
            ),
        options
    },{
        type: 'number', 
        renderString: "renderCell",    
    });
}

export const createPercentageDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) =>
            typeof value === "number" ? (
                <Box component="span" sx={{ display: "block", minWidth: "max-content" }}>
                    {`${value} %`}{" "}
                </Box>
            ) : (
                "N/A"
            ),
        options
    },{
        type: 'number', 
        renderString: "renderCell",    
    });
}

export const createBooleanPayerDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            if (typeof value === 'boolean') {
                if (value) {
                    return 'Payer';
                } else {
                    return 'Vendor';
                }
            }
            return '';
        },
        options
    };
}

export const createTypeMetadataDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: string) => {
            let metadata = {};
            try {
                metadata = JSON.parse(value);
            }
            catch {
                metadata = {};
            }

            if (metadata) {
                const fileExtension = (metadata as any).file_extension;
                if (fileExtension) {
                    // Remove the dot and convert to uppercase
                    return fileExtension.replace('.', '').toUpperCase();
                }
                return (metadata as any).type || '';
            }
            return '';
        },
        options
    };
}

export const createDocumentStatusChipField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
        switch (status?.toLowerCase()) {
            case 'ready': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'error';
            default: return 'default';
        }
    };

    const capitalizeFirstLetter = (str: string) => {
        if (!str) return 'Unknown';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    return {
        id,
        label,
        renderString: (value: string) => (
            <Chip 
                label={capitalizeFirstLetter(value)} 
                color={getStatusColor(value)}
                size="small"
                variant="outlined"
            />
        ),
        options: {
            ...options,
            sortable: false,
            hideFromGrid: false,
            hideFromFilter: false,
        },
    };
}

export const createFloatingPoint = (value: number) => {
    if (value) {
        return value.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    return 0.00;
}

export const createPayMethodDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            const maybeRow = value && (
                value.paymethod !== undefined ||
                value.rulePaymentFeeScheduleXRef !== undefined ||
                value.rulePaymentFeeScheduleXref !== undefined ||
                value.ruleFeeScheduleGroupXref !== undefined
            ) ? value : null;

            if (maybeRow) {
                // SS02-616: Check for group-based architecture first (Schedule pay source)
                // Groups contain nested payment arrays (NOT split - splits show as "Split Unit")
                let xrefs = [];

                if (maybeRow.ruleFeeScheduleGroupXref && Array.isArray(maybeRow.ruleFeeScheduleGroupXref) && maybeRow.ruleFeeScheduleGroupXref.length > 0) {
                    // SCHEDULE PAY SOURCE: Extract payment data from ALL groups and flatten
                    // Only read from rulePaymentFeeScheduleXRef (NOT splits)
                    maybeRow.ruleFeeScheduleGroupXref.forEach((group: any) => {
                        if (group.rulePaymentFeeScheduleXRef && Array.isArray(group.rulePaymentFeeScheduleXRef)) {
                            xrefs.push(...group.rulePaymentFeeScheduleXRef);
                        }
                    });
                } else {
                    // CHARGE PAY SOURCE: Check direct payment array only (NOT splits)
                    xrefs = maybeRow.rulePaymentFeeScheduleXRef ?? maybeRow.rulePaymentFeeScheduleXref ?? [];
                }

                // Extract pay method names from collected xrefs
                if (Array.isArray(xrefs) && xrefs.length > 0) {
                    const names = xrefs
                        .map((x: any) => x?.paymethod?.name ?? x?.payMethod?.name ?? x?.payMethodName ?? x?.paymethodName)
                        .filter(Boolean);
                    if (names.length > 0) {
                        return names.join(', ');
                    }
                }

                // Fallback: Direct paymethod field on Rule (Charge non-split)
                const legacy = maybeRow.paymethod;
                if (legacy && typeof legacy === 'object') return legacy.name ?? '';
                if (typeof legacy === 'string') return legacy;
                return '';
            }

            if (value && typeof value === 'object') return value.name ?? '';
            if (typeof value === 'string') return value;
            return '';
        },
        options: { ...options, returnWholeRow: true },
    };
}

export const createPayValueDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            const maybeRow = value && (
                value[id] !== undefined ||
                value.rulePaymentFeeScheduleXRef !== undefined ||
                value.rulePaymentFeeScheduleXref !== undefined ||
                value.ruleFeeScheduleGroupXref !== undefined
            ) ? value : null;

            if (maybeRow) {
                // SS02-616: Check for group-based architecture first (Schedule pay source)
                // Groups contain nested payment arrays (NOT split - splits handled separately)
                let xrefs = [];

                if (maybeRow.ruleFeeScheduleGroupXref && Array.isArray(maybeRow.ruleFeeScheduleGroupXref) && maybeRow.ruleFeeScheduleGroupXref.length > 0) {
                    // SCHEDULE PAY SOURCE: Extract payment data from ALL groups and flatten
                    // Only read from rulePaymentFeeScheduleXRef (NOT splits)
                    maybeRow.ruleFeeScheduleGroupXref.forEach((group: any) => {
                        if (group.rulePaymentFeeScheduleXRef && Array.isArray(group.rulePaymentFeeScheduleXRef)) {
                            xrefs.push(...group.rulePaymentFeeScheduleXRef);
                        }
                    });
                } else {
                    // CHARGE PAY SOURCE: Check direct payment array only (NOT splits)
                    xrefs = maybeRow.rulePaymentFeeScheduleXRef ?? maybeRow.rulePaymentFeeScheduleXref ?? [];
                }

                // Extract pay values from collected xrefs
                if (Array.isArray(xrefs) && xrefs.length > 0) {
                    const vals = xrefs
                        .map((x: any) => x?.payValue ?? x?.payvalue ?? x?.value)
                        .filter(v => v !== undefined && v !== null)
                        .map(v => typeof v === 'number' ? v : String(v));
                    if (vals.length > 0) {
                        return vals.join(', ');
                    }
                }

                // Fallback: Direct payvalue field on Rule (Charge non-split)
                const legacy = maybeRow[id];
                if (typeof legacy === 'number') return legacy;
                if (typeof legacy === 'string') return legacy;
                return '';
            }

            if (typeof value === 'number') return value;
            if (typeof value === 'string') return value;
            return '';
        },
        options: { ...options, returnWholeRow: true },
    };
}

export const createSecondLevelField = <T extends AnyProps>(id: string, label: string, field: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any; }) => (typeof value === 'object' && value) ? value[field] : '',
        options
    },{
        type: 'string', 
        renderString: 'valueGetter',
    });
}

export const createIconDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: any) => {
            if (typeof value === 'boolean') {
                if (value) {
                    return true;
                } else {
                    return false;
                }
            }
            return false;
        },
        options
    },{
        type: 'boolean', 
        renderString: false,    
    });
}

export const createReverseIconDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            if (typeof value === 'boolean') {
                if (value) {
                    return false;
                } else {
                    return true;
                }
            }
            return false;
        },
        options
    };
}

export const createSearchFilterDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: string) => {
            let filters = [];
            try {
                filters = JSON.parse(value);
            }
            catch {
                filters = [];
            }

            if (isArray(filters)) {
                return (
                    <ul style={{ paddingInlineStart: '15px' }} className="mb-0">
                        {
                            filters.map((item, index) => {
                                const equators = [...dateEquators.equatorArr, ...StringEquators.equatorArr];
                                const extractedEquator = equators.find(eq => eq.value === item.equator);
                                return <li key={`${index}-${item.field}`}>
                                    {item.field} <i>{extractedEquator ? extractedEquator.label : item.equator}</i> <b>{item.value}</b></li>
                            })
                        }
                    </ul>
                );

            }
            return '';
        },
        options
    };
}

export const createKPIDataField = <T extends AnyProps>(id: string, label: string, options: T = {} as T, payerScoreCard: undefined) => {
    if (id === 'timeToPay' || id == 'payerYield') {
        return {
            id,
            label,
            renderString: (value: { [x: string]: any; detailData?: any; timeToPayColor?: any; payerYieldColor?: any; }) => {
                const { paidOnTime, totalClaims, payerYieldExpected, payerYieldActual, payerYieldTotal, payerYieldVariance, timeToPayVariance } = value.detailData;
                const { timeToPayColor, payerYieldColor } = value;

                // receiving color from backend
                let className = '';
                if (id === 'timeToPay') {
                    className = timeToPayColor === 'GREEN' ? 'text-white bg-success'
                        : timeToPayColor === 'RED' ? 'bg-danger text-white'
                            : timeToPayColor === 'YELLOW' ? 'bg-warning text-dark' : '';

                } else if (id === 'payerYield') {
                    className = payerYieldColor === 'GREEN' ? 'text-white bg-success'
                        : payerYieldColor === 'RED' ? 'bg-danger text-white'
                            : payerYieldColor === 'YELLOW' ? 'bg-warning text-dark' : '';
                }

                const kpi = value[id];
                const timeToPayHoverData = [
                    {
                        label: 'Paid On Time',
                        data: paidOnTime
                    },
                    {
                        label: 'Total Claims',
                        data: totalClaims
                    },
                    {
                        label: 'Time To Pay Variance',
                        data: timeToPayVariance
                    }
                ];

                const payerYieldPayHoverData = [
                    {
                        label: 'Payer Yield Expected',
                        data: payerYieldExpected
                    },
                    {
                        label: 'Payer Yield Actual',
                        data: payerYieldActual
                    },
                    {
                        label: 'Payer Yield Total',
                        data: payerYieldTotal
                    },
                    {
                        label: 'Payer Yield Variance',
                        data: payerYieldVariance
                    }
                ];


                return <PopoverLink
                    columnData={
                        <Chip
                            style={{ borderRadius: '5px', width: '70px', fontWeight: '600' }}
                            label={id === 'timeToPay' ? kpi : `${kpi}%`}
                            className={className}
                        />
                    }
                    hoverData={id === 'timeToPay' ? timeToPayHoverData : id === 'payerYield' ? payerYieldPayHoverData : []}
                    title={label}
                />
            },
            options
        };

    }
    else {
        return {
            id,
            label,
            renderString: (value: { [x: string]: any; detailData?: any; errorRateColor?: any; denialRateColor?: any; }) => {
                const { errorClaims, deniedClaims, totalClaims } = value.detailData;
                const { errorRateColor, denialRateColor } = value;

                // receiving color from backend
                let className = '';
                if (id === 'errorRate') {
                    className = errorRateColor === 'GREEN' ? 'text-white bg-success'
                        : errorRateColor === 'RED' ? 'bg-danger text-white'
                            : errorRateColor === 'YELLOW' ? 'bg-warning text-dark' : '';

                } else if (id === 'denialRate') {
                    className = denialRateColor === 'GREEN' ? 'text-white bg-success'
                        : denialRateColor === 'RED' ? 'bg-danger text-white'
                            : denialRateColor === 'YELLOW' ? 'bg-warning text-dark' : '';
                }

                const kpi = value[id];

                const errorRateHoverData = [
                    {
                        label: 'Error Claims',
                        data: errorClaims
                    },
                    {
                        label: 'Total Claims',
                        data: totalClaims
                    },
                ];
                const denialRateHoverData = [
                    {
                        label: 'Denied Claims',
                        data: deniedClaims
                    },
                    {
                        label: 'Total Claims',
                        data: totalClaims
                    },
                ]

                return <PopoverLink
                    columnData={
                        <Chip
                            style={{ borderRadius: '5px', width: '70px', fontWeight: '600' }}
                            label={`${kpi}%`}
                            className={className}
                        />
                    }
                    hoverData={id === 'errorRate' ? errorRateHoverData : id === 'denialRate' ? denialRateHoverData : []}
                    title={label}
                />
            },
            options
        };
    }
}

export const createExceptionHoverField = <T extends AnyProps>(id: string, label: string, equalTo: string | boolean, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: any; exception: any; }) => {
            if (value[id] === equalTo) {
                return <i style={{ fontSize: '1.3rem' }} className="fas fa-check-circle text-success ml-2"></i>
            } else {
                return <PopoverLink
                    columnData={
                        <ErrorIcon className="ml-2" color="error" />
                    }
                    hoverData={[{ label: value.exception }]}
                    title={'Exception Details'}
                />;
            }
        },
        options
    };
}


export const createStatusField = <T extends AnyProps>(
    id: string,
    label: string,
    options: T = {} as T
) => {
    return transformOptions(
        {
            id,
            label,
            renderString: (value: any) => {
                // status value (string | object | boolean)
                const rawStatus =
                    typeof value === "string"
                        ? value
                        : value?.[id];

                const statusValue = rawStatus ?? "Pending";
                const statusLower = String(statusValue).toLowerCase();

                // Determine Chip color (case-insensitive + boolean safe)
                let color: ComponentProps<typeof Chip>["color"] =
                    ["completed", "success", "read", "processed", "ready"].includes(statusLower) ||
                    statusValue === true
                        ? "secondary"
                        : ["failed", "unread"].includes(statusLower) ||
                          statusValue === false
                        ? "error"
                        : "primary";

                // label (altValues support)
                const chipLabel = options?.altValues
                    ? options.altValues[String(statusValue)]
                    : statusValue;

                // Handle IGNORED + link case
                if (options?.haveLink && statusValue === "IGNORED") {
                    return (
                        <Box sx={{ display: "flex", minWidth: "max-content" }}>
                            <Chip
                                sx={{
                                    borderRadius: "5px",
                                    width: "90px",
                                    height: "18px",
                                    fontWeight: "400",
                                    paddingTop: "1px",
                                    mr: "10px",
                                }}
                                label={chipLabel}
                                color={color}
                            />
                            <Link
                                to={`/cms/claimLookup/${value?.claimnumber}`}
                                style={{ textDecoration: "underline" }}
                            >
                                View Details
                            </Link>
                        </Box>
                    );
                }

                // Default Chip rendering
                return (
                    <Chip
                        sx={{
                            borderRadius: "5px",
                            width: "90px",
                            height: "18px",
                            fontWeight: "400",
                            paddingTop: "1px",
                        }}
                        label={chipLabel}
                        color={color}
                    />
                );
            },
            options,
        },
        {
            type: "string",
            renderString: "renderCell",
        }
    );
};


export const createUploadsStatusField = <T extends AnyProps>(id: string, label: string, textKey: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal; }) => {
            let color : ComponentProps<typeof Chip>['color'] = 
                value[id] === 'PROCESSED' ? 'secondary'
                : value[id] === 'FAILED' ? 'error'
                : 'primary';

            if (value[id] === 'FAILED') {
                return <PopoverLink
                    columnData={
                        <Chip
                            style={{ borderRadius: '5px', width: '90px', height: '18px', fontWeight: '400', paddingTop: '1px' }}
                            label={value[id]}
                            color={color}
                        />
                    }
                    hoverData={[{ label: value[textKey ?? 'exception'] }]}
                    title={'Exception Details'}
                />;
            } else {
                return <Chip
                    style={{ borderRadius: '5px', width: '90px', height: '18px', fontWeight: '400', paddingTop: '1px' }}
                    label={value[id]}
                    color={color}                />
            }
        },
        options
    };
}

export const createListField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            // Handle when value is already an array (direct value)
            if (Array.isArray(value)) {
                return (
                    value.map((item: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal, index: { toString: () => any; }) => {
                        return (
                            <li key={`${item}-${index.toString()}`}>
                                {item}
                            </li>
                        )
                    })
                )
            }
            
            // Handle when value is an object with id property
            if (value && value[id] && Array.isArray(value[id])) {
                return (
                    value[id].map((item: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal, index: { toString: () => any; }) => {
                        return (
                            <li key={`${item}-${index.toString()}`}>
                                {item}
                            </li>
                        )
                    })
                )
            }
            
            return '';
        },
        options
    };
}


export const createContractListField = <T extends { class?: string }>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { contractFamilyXref: any[]; }) => {
            if (value) {
                const contracts = value.contractFamilyXref?.sort((a: { precedence: number; }, b: { precedence: number; }) => a.precedence - b.precedence);

                if (contracts.length > 5) {
                    const visibleContracts = contracts.slice(0, 5);
                    const hiddenContracts = contracts.slice(5);

                    return (
                        <>
                            {visibleContracts.map((item: { contractName: string | number | boolean | {} | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactNodeArray | React.ReactPortal; }, index: number) => (
                                <li key={`${item}-${index.toString()}`}>
                                   {item?.contractName} {index===visibleContracts.length-1 && <span className={options.class}>...</span>}
                                </li>
                            ))}
                        </>
                    );
                } else {
                    return contracts.map((item: { contractName: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal; }, index: { toString: () => any; }) => (
                        <li key={`${item}-${index.toString()}`}>
                            {item?.contractName}
                        </li>
                    ));
                }
            }
            return '';
        },
        options
    };
}


export const createTooltipIconField = <T extends AnyProps>(id: string, label: string, options: T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {
            const tooltipTitle = value[id] ? options.checkedTooltip : options.uncheckedTooltip;
            return <MaterialTooltip title={tooltipTitle}>
                <i className={`${options.iconClass} ${value[id] ? 'text-success' : 'text-secondary'} ml-2 font-20`}></i>
            </MaterialTooltip>
        },
        options
    },{
        type: 'boolean', 
        renderString: 'renderCell'
    });
}

export const createTooltipIconForEmptyField = <T extends AnyProps>(id: string, label: string, options: T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {
            if(value[id]) {
                return value[id];
            }
            return <MaterialTooltip title={options.errorTooltip}>
                <i className={`${options.iconClass} ${options.iconColor} ml-2 font-20`}></i>
            </MaterialTooltip>
        },
        options
    };
}

export const createTooltipIconForNonEmptyField = <T extends AnyProps>(id: string, label: string, options: T) => {  
    return {
        id,
        label,
        renderString: (value: any) => {
            if(value) {
                return <MaterialTooltip title={options.errorTooltip}>
                    <i className={`${options.iconClass} ${options.iconColor} ml-2 font-20`}></i>
                </MaterialTooltip>
            }
            else {
                return '';
            }
            
        },
        options
    };
}

export const createContractStateField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: {}) => {
            let className = value === CONTRACT_STATE_FINALIZED ? 'contract-badge-green'
                : value === CONTRACT_STATE_MODEL ? 'contract-badge-orange'
                    : 'contract-badge-blue';

            return <Chip
                style={{ borderRadius: '5px', width: '80px', height: '18px', fontWeight: '600', paddingTop: '0px' }}
                label={value}
                className={className}
            />
        },
        options
    };
}

export const createContractRuleTypeField = <T extends AnyProps>(id: string, label: string, field: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {
            let ruleType = (typeof value === 'object' && value) ? value[field] : '';
            let className = ruleType === RULE_TYPE_CARVEOUT ? 'contract-badge-green'
                : ruleType === RULE_TYPE_RULE ? 'contract-badge-orange'
                : ruleType === RULE_TYPE_DEFAULT ? 'contract-badge-blue'
                : ruleType === CONFIG_TYPE_EAPG ? 'contract-badge-purple'
                : ruleType === CONFIG_TYPE_SNF ? 'contract-badge-teal'
                : ruleType === CONFIG_TYPE_IRF ? 'contract-badge-brown'
                : ruleType === CONFIG_TYPE_IPF ? 'contract-badge-rust'
                : ruleType === CONFIG_TYPE_LTCH ? 'contract-badge-grey'
                    : 'contract-badge-red';

            return <Chip
                style={{ borderRadius: '5px', width: '100px', height: '18px', fontWeight: '600', paddingTop: '0px' }}
                label={ruleType}
                className={className}
            />
        },
        options
    };
}


export const createClaimAnalysisStatusField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: string; }) => {
            let status = value[id] || '';
            let className = status === "New" ? 'contract-badge-green'
                : status === "InProgress" ? 'contract-badge-orange'
                : status === "ClosedRecovered" ? 'contract-badge-blue'
                : status === "ClosedIgnored" ? 'contract-badge-purple'
                    : 'contract-badge-red';

            return <Chip
                style={{ borderRadius: '5px', width: '100px', height: '18px', fontWeight: '600', paddingTop: '0px' }}
                label={status}
                className={className}
                title={status}
            />
        },
        options
    },{
        type: 'string', 
        renderString: 'renderCell'
    });
}

export const createColumnsSumField = <T extends AnyProps>(id: string, label: string, keyArr: string[], options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: number; }) => {
            let sum = 0;
            for (let item of keyArr) {
                sum = sum + value[item];
            }
            return sum;
        },
        options
    };
}

export const createModifiersFieldWithColons = <T extends AnyProps>(id: string, label: string, options : T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: string | number | boolean | {} | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactNodeArray | React.ReactPortal; }) => {
            if (value) {
                let result = [];

                options.list.forEach((item: { id: React.Key; }, index: number) => {
                    if (value[item.id]) {
                        result.push(
                            <span key={item.id}>
                                {value[item.id]}
                                {index < options.list.length - 1 && <strong> : </strong>}
                            </span>
                        );
                    }
                });

                return result;
            }

            return null;
        },
        options
    };
}

export const createClientProductFlags = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { products: { [x: string]: any; }; }) => {
            return <span>
                Dashboards: <i style={{ fontSize: '1.3rem' }} className={value?.products?.[id] ? "fas fa-check-circle ml-2 text-success" : "fas fa-check-circle text-secondary ml-2"}></i>
            </span>
        },
        options
    };
}

export const createAPIKeyField = <T extends { appId?: boolean }>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: any) => {
            return <APIKeyShowComponent value={value} isAppId={options.appId} />
        },
        options
    };
}

export const createContractModelField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: { [x: string]: any; }) => {
            if (typeof value === 'object') {
                if (value) {
                    if(value["ExecutionError"] && value["ExecutionError"] !== '') {
                        return  <PopoverLink
                                    columnData={
                                        <ErrorIcon className="ml-2" color="error" />
                                    }
                                    hoverData={[{ label: value["ExecutionError"] }]}
                                    title={'Error Details'}
                                />;
                    }
                    else 
                        return (typeof value["ExpectedPay"] === 'number') ? `$ ${createFloatingPoint(value["ExpectedPay"])}` : "N/A" ;
                }
                return 'N/A';
            }
        },
        options
    };
}

export const createExecutionStatusField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return {
        id,
        label,
        renderString: (value: string) => {

            let color : ComponentProps<typeof Chip>['color'] =
            value === "COMPLETED" ? "secondary" : 
            (value === 'INPROGRESS' || value === 'SUBMITTED') ? "primary" : 
            value === "FAILED" ? "error" : 
            "default"

            return <Chip
                style={{ borderRadius: '5px', width: '90px', height: '18px', fontWeight: '400', paddingTop: '1px' }}
                label={value}
                color={color}                
            />
        },
        options
    };
}

const usDollarsFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});
  
export function asDollars( amount: number ) : string {
    return amount == null ? '' : 
        amount === 0 ? '-' :
        usDollarsFormatter.format(amount);
}
export function asDollarsWithZero( amount: number ) : string {
    return amount == null ? '' :
        usDollarsFormatter.format(amount);
}
/**
 * Converts a fraction to a percentage string.
 *
 * @param fraction - The fraction to convert. If the fraction is null or undefined, an empty string is returned.
 * @returns The percentage representation of the fraction, rounded to the nearest whole number, followed by a '%' sign.
 */
export function asPercentage( fraction: number ) : string{
    return fraction == null ? '' :
        Math.abs( fraction ) < 0.000001 ? '-' :
        `${Math.round(fraction * 100)}%`;
}

export const createRawHtmlField = <T extends AnyProps>(id: string, label: string, options: T = {} as T) => {
    return transformOptions({
        id,
        label,
        renderString: (value: { [x: string]: any }) => {
             let content = "";
             if (typeof value === 'string' || typeof value === 'number') {
                 content = String(value);
             } else if (typeof value === 'object' && value !== null) {
                  const { secondLevel } = options;
                  if (options?.thirdLevel && value[secondLevel]) {
                       content = value[secondLevel][options.thirdLevel];
                  } else if (value[secondLevel]) {
                       content = value[secondLevel];
                  }
             }

             if (!content) return "";
             const MAX_LENGTH = 50;
             const MSG_MAX_LENGTH = 200;
             const isLong = (label === "Details" && content.length > MAX_LENGTH) || (label === "Message" && content.length > MSG_MAX_LENGTH);
             const shortText = isLong
                ? content.substring(0, MAX_LENGTH) + "..."
                : content;
            const span = (
                <span
                    dangerouslySetInnerHTML={{
                        __html: shortText + (isLong ? ' <span style="color:#1890ff">Show more</span>' : '')
                    }}
                    style={{
                        width: options?.width || 'inherit',                        
                        wordBreak: 'break-word',
                        cursor: isLong ? 'pointer' : 'default'
                    }}
                />
            );
            if (isLong) {                
                return (
                    <MaterialTooltip
                        title={
                            <div>
                                <div
                                    style={{ fontWeight: 600, marginBottom: 5 }}
                                >
                                    {"Details:"}
                                </div>
                                <div style={{ borderBottom: "1px solid #eee", marginBottom: 6 }} />
                                <div
                                    style={{
                                        maxHeight: "300px",
                                        overflowY: "auto",
                                        paddingRight: "5px",
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: content,
                                    }}
                                />                                
                            </div>                            
                        }
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: "#fff",
                                    color: "#333",
                                    fontSize: "13px",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"                                    
                                },
                            },
                        }}
                    >
                        {span}
                    </MaterialTooltip>
                );
            }
            return span;            
        },
        options
    }, {
        type: 'string',
        renderString: 'renderCell'
    });
}